import React, { useState, useEffect, useRef } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useAppSelector, useAppDispatch } from "./app/hooks";
import { useProjects } from "./features/projects/projectSlice";
import { AppType } from "@common/types/sharedTypes";
import { selectUser, useUser, selectAccess } from "./features/user/userSlice";
import { selectConfig } from "./features/config/configSlice";
import {
  selectCreateNew,
  setCreateNew,
  addAlert,
  selectModalById,
  selectLastCreateMtrl,
  setLastCreateMtrl,
} from "./features/ui/uiSlice";
import i18n from "../i18n";
import { User } from "./features/user/userSlice";
import { useTranslation } from "react-i18next";
import { KeywordPillsHandle } from "./components/ui-components/SXKeywordPill";
import { useAuth0 } from "@auth0/auth0-react";
import { getClientId } from "./App";
import LeftPanel from "./components/ProjectExplorer/LeftPanel";
import CenterPanel from "./components/ProjectExplorer/CenterPanel";
import RightPanel from "./components/ProjectExplorer/RightPanel";
import { ShareModal } from "./components/modals/ShareModal";

interface ProjectExplorerProps {
  lang?: string;
  user?: User;
}

export interface Project {
  id: number;
  guid: string;
  name: string;
  description: string;
  beamAmount?: number; // Optional field for beam amount
  foundationAmount?: number; // Optional field for foundation amount
  users?: User[]; // Optional field for users
  status?: string; // Optional field for project status
  files: ProjectFile[];
}

export interface ProjectFile {
  id: number;
  name: string;
  app: AppType | string;
  company?: string;
  created?: string; // Optional field for created date
  lastModified?: string; // Optional field for last modified date
  modifiedBy?: string; // Optional field for modified by
  author?: string; // Optional field for author
  signature?: string; // Optional field for signature
  type?: AppType | string; // Optional field for file type
  description?: string; // Optional field for file description
  status?: string; // Optional field for file status
  comments?: string; // Optional field for file comments
  keywords?: string[]; // Optional field for keywords
  guid?: string; // Optional field for file guid (for opening the file link)
  maxUtilizationPercent?: number | null;
}

export default function ProjectExplorer(props: ProjectExplorerProps) {
  // Parses a UTC date string reliably, appending 'Z' if no timezone indicator is present.
  const parseUTC = (dateStr: string | undefined): Date => {
    if (!dateStr) return new Date(0);
    return /[Zz]$|[+-]\d{2}:?\d{2}$/.test(dateStr) ? new Date(dateStr) : new Date(dateStr + "Z");
  };
  const isShareModalOpen = useAppSelector(selectModalById("shareModal"));
  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProjectDeleteModal, setShowProjectDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: number; type: AppType | null; guid: string } | null>(null);
  const { t } = useTranslation();
  // State for multi-delete confirmation modal
  const [showMultiDeleteModal, setShowMultiDeleteModal] = useState(false);
  const [pendingMultiDelete, setPendingMultiDelete] = useState<
    { id: number; type: AppType; guid: string; name?: string }[]
  >([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<number>(-1);
  const [nextFileListName, setNextFileListName] = useState<string>("");
  const user: User = useAppSelector(selectUser);
  const isCreatingNew = useAppSelector(selectCreateNew);
  const access = useAppSelector(selectAccess);
  const [copyOFCurrentSelectedFile, setCopyOfCurrentSelectedFile] = useState<ProjectFile | null>(null);
  const config = useAppSelector(selectConfig);
  const [showSameNameWarning, setShowSameNameWarning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const fileTypeRef = React.useRef<HTMLSelectElement>(null);
  // Track checked checkboxes for multi-select delete
  const [checkedCount, setCheckedCount] = useState(0);
  const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
  // Shift/Cmd/Ctrl-click multi-selection of whole projects in the LeftPanel (for bulk delete).
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [keywordInputDirty, setKeywordInputDirty] = useState(false);
  const keywordPillsRef = useRef<KeywordPillsHandle>(null);

  const {
    deleteProject,
    updateProject,
    createProject,
    uploadProjectFile,
    updateFileMeta,
    updateFileProject,
    downloadProject,
    copyProject,
    deleteMultipleProjects,
    updateActiveDeactive,
    updateActiveDeactiveProject,
    deleteProjectandFiles,
  } = useProjects();
  const [draggedOverFile, setDraggedOverFile] = useState<ProjectFile | null>(null);
  const [isFilesDragOver, setIsFilesDragOver] = useState(false);
  const [isProjectDragOver, setIsProjectDragOver] = useState(false);
  const { getUser, checkAccessUser } = useUser();
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  const configs_ui = useAppSelector(selectConfig);
  // In the store rather than local state, so the material survives leaving this page and is still
  // picked the next time "New file" is opened.
  const mtrlTemplate = useAppSelector(selectLastCreateMtrl);
  const setMtrlTemplate = (mtrl: number | undefined) => dispatch(setLastCreateMtrl(mtrl));
  const [projectName, setProjectName] = useState<string>("");
  const [sfileStatus, setSfileStatus] = useState<string>("active");
  const queryParams = new URLSearchParams(window.location.search);
  const projidParams = queryParams.get("proj") ?? "-1";

  // Status for Beam and Foundation backend check

  useEffect(() => {
    (async () => {
      await checkAccessUser();
    })();
  }, []);

  useEffect(() => {
    if (isCreatingNew || selectedProjectName) return;

    const projects = Object.values(user.projectdigest || {}) as Project[];
    if (projects.length === 0) return;

    if (projidParams && projidParams !== "-1") {
      for (const project of projects) {
        const fileIndex = project.files.findIndex((file) => file.guid === projidParams);
        if (fileIndex > -1) {
          setSelectedProjectName(project.guid);
          setSfileStatus(project.status ?? "active");
          setSelectedFile(fileIndex);
          setCopyOfCurrentSelectedFile({ ...project.files[fileIndex] });
          return;
        }
      }
    }

    const firstProject = projects[0];
    setSelectedProjectName(firstProject.guid);
    setSfileStatus(firstProject.status ?? "active");

    if (firstProject.files.length > 0) {
      setSelectedFile(0);
      setCopyOfCurrentSelectedFile({ ...firstProject.files[0] });
    } else {
      setSelectedFile(-1);
      setCopyOfCurrentSelectedFile(null);
    }
  }, [isCreatingNew, selectedProjectName, user.projectdigest, projidParams]);

  useEffect(() => {
    setSelectedFileIds([]);
    setCheckedCount(0);
  }, [selectedProjectName]);

  useEffect(() => {
    setShowSameNameWarning(false);
  }, [selectedFile, selectedProjectName]);

  useEffect(() => {
    setCheckedCount(selectedFileIds.length);
  }, [selectedFileIds]);
  useEffect(() => {
    if (isCreatingNew) {
      setSelectedFile(-1);
      setTimeout(() => {
        if (fileTypeRef.current) {
          fileTypeRef.current.focus();
        }
      }, 0);
    }
  }, [isCreatingNew]);

  useEffect(() => {
    if (user && user.projectdigest) {
      if (!isCreatingNew && projectName && selectedProjectName != null) {
        setCreateNew(null);
        let index =
          user?.projectdigest[selectedProjectName]?.files?.findIndex((file) => {
            return file.name == projectName;
          }) ?? -1;

        if (index > -1) {
          setCopyOfCurrentSelectedFile({ ...user?.projectdigest[selectedProjectName]?.files[index] }); // Keep a copy of the currently selected file
          setProjectName("");
          setSelectedFile(index);
          dispatch(setCreateNew(null));
        }
      }
    }
  }, [user, projectName]);

  const onChangeFileFields = (field: string, value: any) => {
    console.log("Updating file field", field, value);
  };

  const parseAppType = (value: unknown): AppType | null => {
    if (typeof value === "number" && Object.values(AppType).includes(value as AppType)) {
      return value as AppType;
    }
    if (typeof value === "string") {
      const numeric = Number(value);
      if (!Number.isNaN(numeric) && Object.values(AppType).includes(numeric as AppType)) {
        return numeric as AppType;
      }
      const normalized = value.trim();
      if (normalized === "Simplex Beam" || normalized === "SIMPLEX_APP_BEAM") return AppType.SIMPLEX_APP_BEAM;
      if (normalized === "Simplex Foundation" || normalized === "SIMPLEX_APP_FOUNDATION")
        return AppType.SIMPLEX_APP_FOUNDATION;
      if (normalized === "Simplex Column" || normalized === "SIMPLEX_APP_COLUMN") return AppType.SIMPLEX_APP_COLUMN;
      if (normalized === "Simplex Frame" || normalized === "SIMPLEX_APP_FRAME") return AppType.SIMPLEX_APP_FRAME;
      if (normalized === "Simplex Section" || normalized === "SIMPLEX_APP_SECTION") return AppType.SIMPLEX_APP_SECTION;
      if (normalized === "Simplex Retaining Wall" || normalized === "SIMPLEX_APP_RETAININGWALL")
        return AppType.SIMPLEX_APP_RETAININGWALL;
      if (normalized === "Simplex Basement Wall" || normalized === "SIMPLEX_APP_BASEMENT_WALL")
        return AppType.SIMPLEX_APP_BASEMENT_WALL;
      if (normalized === "Simplex Pile" || normalized === "SIMPLEX_APP_PILE") return AppType.SIMPLEX_APP_PILE;
      if (normalized === "Simplex CPT" || normalized === "SIMPLEX_APP_CPT") return AppType.SIMPLEX_APP_CPT;
    }
    return null;
  };

  let sfile: ProjectFile | null = null;
  if (selectedProjectName != null) {
    if (selectedFile != -1) {
      if (user.projectdigest[selectedProjectName] && user.projectdigest[selectedProjectName].files.length > 0) {
        sfile = user.projectdigest[selectedProjectName].files[selectedFile];
      }
    }
  }

  const selectedFileAppType = parseAppType(sfile?.type);

  const getSelectedFilesForCurrentProject = () => {
    const selectedProjectFiles = selectedProjectName ? user.projectdigest[selectedProjectName]?.files || [] : [];
    const focusedFileId = sfile?.id;

    if (selectedFileIds.length > 0) {
      return selectedProjectFiles.filter((file) => selectedFileIds.includes(file.id));
    }

    if (focusedFileId !== undefined) {
      return selectedProjectFiles.filter((file) => file.id === focusedFileId);
    }

    return [];
  };

  const openMultiDeleteModalForSelectedFiles = () => {
    const filesToDelete = getSelectedFilesForCurrentProject().flatMap((file) => {
      const parsedType = parseAppType(file.type);
      if (parsedType === null || !file.guid) {
        return [];
      }

      return [
        {
          id: file.id,
          type: parsedType,
          guid: file.guid,
          name: file.name,
        },
      ];
    });

    setPendingMultiDelete(filesToDelete);
    setShowMultiDeleteModal(true);
  };

  useEffect(() => {
    const fileStatus = (sfile?.keywords || [])
      .map((k) => {
        try {
          return JSON.parse(k);
        } catch {
          return null;
        }
      })
      .find((obj) => obj && obj.status);
    //console.log("File status from keywords:", fileStatus);
    //  setSfileStatus(fileStatus?.status);
  }, [sfile]);

  if (isCreatingNew) {
    sfile = null;
  }

  const showSaveButton =
    !isCreatingNew &&
    !!sfile &&
    !!copyOFCurrentSelectedFile &&
    !showSameNameWarning &&
    (keywordInputDirty ||
      sfile.name !== copyOFCurrentSelectedFile.name ||
      (sfile.description ?? "") !== (copyOFCurrentSelectedFile.description ?? "") ||
      (sfile.comments ?? "") !== (copyOFCurrentSelectedFile.comments ?? "") ||
      JSON.stringify(sfile.keywords ?? []) !== JSON.stringify(copyOFCurrentSelectedFile.keywords ?? []));

  const refreshSelectedProject = () => {
    if (selectedProject) {
      (Object.values(user.projectdigest) as Project[]).forEach((project) => {
        if (project.id === selectedProject.id) {
          setSelectedProjectName(project.guid);
          let newfileindex = project.files.findIndex((file) => file.id === selectedFile);
          if (newfileindex !== -1) {
            setSelectedFile(newfileindex); // Update selected file if it exists in the project
          } else if (project.files.length > 0) {
            setSelectedFile(0);
          } // Select the first file by default
        }
      });
    }
  };

  const handlePrint = async (printTemplate: "reduced" | "normal" | "full") => {
    if (!sfile?.guid || isPrinting || selectedFileAppType === null) return;
    const PRINT_REQUEST_TIMEOUT_MS = 120_000;
    const printTab = window.open("about:blank", "_blank");
    if (printTab) {
      printTab.document.title = "Preparing PDF...";
      printTab.document.body.innerHTML = `
        <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f5f7fa; color: #1f2937;">
          <div style="text-align: center; max-width: 420px; padding: 24px;">
            <div style="width: 28px; height: 28px; border: 3px solid #cbd5e1; border-top-color: #2563eb; border-radius: 50%; margin: 0 auto 14px auto; animation: sx-spin 0.9s linear infinite;"></div>
            <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Preparing your PDF</h1>
            <p style="font-size: 14px; line-height: 1.4; margin: 0; color: #4b5563;">This can take a few seconds for larger files. This tab will update automatically when the document is ready.</p>
          </div>
        </div>
        <style>
          @keyframes sx-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        </style>
      `;
    } else {
      dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text: t("print_error") || "Popup was blocked. Please allow popups and try again.",
          status: "danger",
        }),
      );
      return;
    }
    setIsPrinting(true);
    const printRequestController = new AbortController();
    const printRequestTimeoutId = window.setTimeout(() => {
      printRequestController.abort();
    }, PRINT_REQUEST_TIMEOUT_MS);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch("/api/print/start", {
        method: "POST",
        signal: printRequestController.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Client-ID": String(getClientId()),
        },
        body: JSON.stringify({
          fileGuid: sfile.guid,
          appType: selectedFileAppType,
          projectName: selectedProjectName || "",
          fileName: sfile.name || "",
          printTemplate,
        }),
      });
      window.clearTimeout(printRequestTimeoutId);
      if (!response.ok) {
        throw new Error(`Print failed (${response.status})`);
      }
      const payload = (await response.json()) as { viewerUrl?: string };
      if (!payload.viewerUrl) {
        throw new Error("Missing print viewer URL in response");
      }
      if (printTab && !printTab.closed) {
        printTab.document.title = "PDF Ready";
        printTab.location.replace(payload.viewerUrl);
      }
    } catch (err) {
      window.clearTimeout(printRequestTimeoutId);
      console.error("Print client error:", err);
      if (printTab && !printTab.closed) {
        const isTimeout = err instanceof DOMException && err.name === "AbortError";
        printTab.document.title = "PDF generation failed";
        printTab.document.body.innerHTML = `
          <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f8fafc; color: #111827;">
            <div style="text-align: center; max-width: 480px; padding: 24px;">
              <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Could not prepare PDF</h1>
              <p style="font-size: 14px; line-height: 1.4; margin: 0 0 14px 0; color: #4b5563;">${isTimeout ? "The request timed out while preparing your PDF. Please try again." : "An error occurred while preparing your PDF. Please close this tab and try again."}</p>
              <button type="button" onclick="window.location.href='${window.location.href}'" style="appearance: none; border: 0; border-radius: 8px; background: #2563eb; color: #fff; padding: 10px 16px; font-size: 14px; font-weight: 600; cursor: pointer;">Retry From App</button>
            </div>
          </div>
        `;
      }
      dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text:
            err instanceof DOMException && err.name === "AbortError"
              ? t("print_error") || "PDF generation timed out. Please try again."
              : t("print_error") || "Failed to generate PDF. Please try again later.",
          status: "danger",
        }),
      );
    } finally {
      setIsPrinting(false);
    }
  };

  const handleFramePrint = async (size: "minimum" | "normal" | "extended") => {
    if (!sfile?.guid || isPrinting || selectedFileAppType === null) return;
    const PRINT_REQUEST_TIMEOUT_MS = 120_000;
    const printTab = window.open("about:blank", "_blank");
    if (printTab) {
      printTab.document.title = "Preparing PDF...";
      printTab.document.body.innerHTML = `
        <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f5f7fa; color: #1f2937;">
          <div style="text-align: center; max-width: 420px; padding: 24px;">
            <div style="width: 28px; height: 28px; border: 3px solid #cbd5e1; border-top-color: #2563eb; border-radius: 50%; margin: 0 auto 14px auto; animation: sx-spin 0.9s linear infinite;"></div>
            <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Preparing your PDF</h1>
            <p style="font-size: 14px; line-height: 1.4; margin: 0; color: #4b5563;">This can take a few seconds. This tab will update automatically when the document is ready.</p>
          </div>
        </div>
        <style>
          @keyframes sx-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        </style>
      `;
    } else {
      dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text: t("print_error") || "Popup was blocked. Please allow popups and try again.",
          status: "danger",
        }),
      );
      return;
    }
    setIsPrinting(true);
    const printRequestController = new AbortController();
    const printRequestTimeoutId = window.setTimeout(() => {
      printRequestController.abort();
    }, PRINT_REQUEST_TIMEOUT_MS);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch("/api/print/frame-start", {
        method: "POST",
        signal: printRequestController.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Client-ID": String(getClientId()),
        },
        body: JSON.stringify({
          fileGuid: sfile.guid,
          appType: selectedFileAppType,
          size,
          fileName: sfile.name || "",
        }),
      });
      window.clearTimeout(printRequestTimeoutId);
      if (!response.ok) {
        throw new Error(`Print failed (${response.status})`);
      }
      const payload = (await response.json()) as { viewerUrl?: string };
      if (!payload.viewerUrl) {
        throw new Error("Missing print viewer URL in response");
      }
      if (printTab && !printTab.closed) {
        printTab.document.title = "PDF Ready";
        printTab.location.replace(payload.viewerUrl);
      }
    } catch (err) {
      window.clearTimeout(printRequestTimeoutId);
      console.error("Frame print client error:", err);
      if (printTab && !printTab.closed) {
        const isTimeout = err instanceof DOMException && err.name === "AbortError";
        printTab.document.title = "PDF generation failed";
        printTab.document.body.innerHTML = `
          <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f8fafc; color: #111827;">
            <div style="text-align: center; max-width: 480px; padding: 24px;">
              <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Could not prepare PDF</h1>
              <p style="font-size: 14px; line-height: 1.4; margin: 0 0 14px 0; color: #4b5563;">${isTimeout ? "The request timed out while preparing your PDF. Please try again." : "An error occurred while preparing your PDF. Please close this tab and try again."}</p>
              <button type="button" onclick="window.location.href='${window.location.href}'" style="appearance: none; border: 0; border-radius: 8px; background: #2563eb; color: #fff; padding: 10px 16px; font-size: 14px; font-weight: 600; cursor: pointer;">Retry From App</button>
            </div>
          </div>
        `;
      }
      dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text:
            err instanceof DOMException && err.name === "AbortError"
              ? t("print_error") || "PDF generation timed out. Please try again."
              : t("print_error") || "Failed to generate PDF. Please try again later.",
          status: "danger",
        }),
      );
    } finally {
      setIsPrinting(false);
    }
  };

  const handleBatchPrint = async (printTemplate: "reduced" | "normal" | "full") => {
    if (isPrinting) return;
    const BATCH_PRINT_TIMEOUT_MS = 180_000;

    const filesToPrint: {
      fileGuid: string;
      appType: AppType;
      projectName: string;
      fileName: string;
      printTemplate: string;
    }[] = [];

    getSelectedFilesForCurrentProject().forEach((fileObj) => {
      if (!fileObj.guid) return;
      const appType = parseAppType(fileObj.type);
      if (appType === null) return;

      filesToPrint.push({
        fileGuid: fileObj.guid,
        appType,
        projectName: selectedProjectName || "",
        fileName: fileObj.name || "",
        printTemplate,
      });
    });

    if (filesToPrint.length < 2) {
      dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text: t("print_error") || "Select at least 2 files to batch print.",
          status: "danger",
        }),
      );
      return;
    }

    const printTab = window.open("about:blank", "_blank");
    if (printTab) {
      printTab.document.title = "Preparing PDF...";
      printTab.document.body.innerHTML = `
        <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f5f7fa; color: #1f2937;">
          <div style="text-align: center; max-width: 420px; padding: 24px;">
            <div style="width: 28px; height: 28px; border: 3px solid #cbd5e1; border-top-color: #2563eb; border-radius: 50%; margin: 0 auto 14px auto; animation: sx-spin 0.9s linear infinite;"></div>
            <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Preparing your PDF</h1>
            <p style="font-size: 14px; line-height: 1.4; margin: 0; color: #4b5563;">Generating a combined document for ${filesToPrint.length} files. This may take a moment.</p>
          </div>
        </div>
        <style>@keyframes sx-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }</style>
      `;
    } else {
      dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text: t("print_error") || "Popup was blocked. Please allow popups and try again.",
          status: "danger",
        }),
      );
      return;
    }

    setIsPrinting(true);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), BATCH_PRINT_TIMEOUT_MS);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch("/api/print/start-batch", {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Client-ID": String(getClientId()),
        },
        body: JSON.stringify({ files: filesToPrint }),
      });
      window.clearTimeout(timeoutId);
      if (!response.ok) {
        throw new Error(`Batch print failed (${response.status})`);
      }
      const payload = (await response.json()) as { viewerUrl?: string };
      if (!payload.viewerUrl) {
        throw new Error("Missing print viewer URL in response");
      }
      if (printTab && !printTab.closed) {
        printTab.document.title = "PDF Ready";
        printTab.location.replace(payload.viewerUrl);
      }
    } catch (err) {
      window.clearTimeout(timeoutId);
      console.error("Batch print error:", err);
      const isTimeout = err instanceof DOMException && err.name === "AbortError";
      if (printTab && !printTab.closed) {
        printTab.document.title = "PDF generation failed";
        printTab.document.body.innerHTML = `
          <div style="font-family: Segoe UI, Arial, sans-serif; min-height: 100vh; margin: 0; display: flex; align-items: center; justify-content: center; background: #f8fafc; color: #111827;">
            <div style="text-align: center; max-width: 480px; padding: 24px;">
              <h1 style="font-size: 18px; font-weight: 600; margin: 0 0 8px 0;">Could not prepare PDF</h1>
              <p style="font-size: 14px; line-height: 1.4; margin: 0 0 14px 0; color: #4b5563;">${isTimeout ? "The request timed out. Please try again." : "An error occurred while preparing your PDF. Please close this tab and try again."}</p>
              <button type="button" onclick="window.close()" style="appearance: none; border: 0; border-radius: 8px; background: #2563eb; color: #fff; padding: 10px 16px; font-size: 14px; font-weight: 600; cursor: pointer;">Close tab</button>
            </div>
          </div>
        `;
      }
      dispatch(
        addAlert({
          id: crypto.randomUUID(),
          type: "danger",
          text: isTimeout
            ? t("print_error") || "Batch PDF generation timed out. Please try again."
            : t("print_error") || "Failed to generate batch PDF. Please try again later.",
          status: "danger",
        }),
      );
    } finally {
      setIsPrinting(false);
    }
  };

  /** Drag n drop handlers */

  const dropHandlerForProjects = async (e: React.DragEvent<HTMLDivElement>, project: Project) => {
    const itemId = e.dataTransfer.getData("application/x-item-id");
    if (!itemId) return;

    let index = project.files.findIndex((f) => f.name === draggedOverFile?.name);
    if (index > -1) {
      dispatch(
        addAlert({
          id: crypto.randomUUID(), // eller egen ID-generator
          type: "danger",
          text: t("fileName_already_exists_in_project"),
          status: "danger",
        }),
      );
      return;
    }

    if (draggedOverFile) {
      await updateFileProject(
        project.guid || "",
        draggedOverFile.guid || "",
        (draggedOverFile.type as AppType) || AppType.SIMPLEX_APP_BEAM,
      );

      await getUser();
    }
  };
  const dropHandler = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    setIsDragging(false);

    if (ev.dataTransfer.items) {
      [...ev.dataTransfer.items].forEach((item, i) => {
        if (item.kind === "file") {
          let projectGuid = user.projectdigest[selectedProjectName || ""]?.guid;
          const droppedFile = item.getAsFile();
          if (droppedFile) {
            uploadProjectFile(
              droppedFile,
              selectedProjectName ? selectedProjectName : null,
              projectGuid ? projectGuid : null,
              (name) => {
                setProjectName(name);
              },
            );
          }
        }
      });
    } else {
      [...ev.dataTransfer.files].forEach((droppedFile, i) => {});
    }
  };

  const dragOverHandler = (ev: React.DragEvent<HTMLDivElement>) => {
    if (ev.dataTransfer.types.includes("Files")) {
      setIsFilesDragOver(true);
      ev.preventDefault(); // allow drop
    }
  };

  const dragEnterHandler = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    setIsDragging(true);
  };

  const dragLeaveHandler = (ev: React.DragEvent<HTMLDivElement>) => {
    ev.preventDefault();
    handleFilesDragLeave;
    setIsDragging(false);
  };

  const handleProjectDragLeave = () => setIsProjectDragOver(false);
  const handleFilesDragLeave = () => setIsFilesDragOver(false);

  /** END/ Drag n drop handlers */

  return (
    // main Component Row
    <Row id="project-explorer" className="forced-scroll gx-1 pt-1 pb-1 ps-2 pe-2">
      <LeftPanel
        t={t}
        setSelectedFile={setSelectedFile}
        setProjectName={setProjectName}
        setSelectedProjectName={setSelectedProjectName}
        setShowProjectDeleteModal={setShowProjectDeleteModal}
        setCopyOfCurrentSelectedFile={setCopyOfCurrentSelectedFile}
        access={access}
        config={config}
        user={user}
        selectedProjectName={selectedProjectName}
        uploadProjectFile={uploadProjectFile}
        parseUTC={parseUTC}
        dropHandlerForProjects={dropHandlerForProjects}
        setIsProjectDragOver={setIsProjectDragOver}
        setDragOverId={setDragOverId}
        dragOverId={dragOverId}
        isProjectDragOver={isProjectDragOver}
        setCheckedCount={setCheckedCount}
        setSelectedFileIds={setSelectedFileIds}
        setSfileStatus={setSfileStatus}
        selectedProjectIds={selectedProjectIds}
        setSelectedProjectIds={setSelectedProjectIds}
        deleteProjectandFiles={deleteProjectandFiles}
        getUser={getUser}
      />

      {/** CENTER PANEL : Project Overview box */}
      {selectedProjectName && user && user.projectdigest[selectedProjectName] && (
        <>
          <CenterPanel
            selectedProjectName={selectedProjectName}
            user={user}
            dropHandler={dropHandler}
            dragOverHandler={dragOverHandler}
            dragEnterHandler={dragEnterHandler}
            dragLeaveHandler={dragLeaveHandler}
            t={t}
            sfileStatus={sfileStatus}
            setSfileStatus={setSfileStatus}
            updateActiveDeactive={updateActiveDeactive}
            updateActiveDeactiveProject={updateActiveDeactiveProject}
            getUser={getUser}
            refreshSelectedProject={refreshSelectedProject}
            showProjectDeleteModal={showProjectDeleteModal}
            setShowProjectDeleteModal={setShowProjectDeleteModal}
            deleteProjectandFiles={deleteProjectandFiles}
            isDragging={isDragging}
            isFilesDragOver={isFilesDragOver}
            isCreatingNew={isCreatingNew}
            setCopyOfCurrentSelectedFile={setCopyOfCurrentSelectedFile}
            access={access}
            config={config}
            uploadProjectFile={uploadProjectFile}
            setProjectName={setProjectName}
            parseUTC={parseUTC}
            parseAppType={parseAppType}
            setDraggedOverFile={setDraggedOverFile}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            setNextFileListName={setNextFileListName}
            setKeywordInputDirty={setKeywordInputDirty}
            copyOFCurrentSelectedFile={copyOFCurrentSelectedFile}
            dispatch={dispatch}
            setCheckedCount={setCheckedCount}
            selectedFileIds={selectedFileIds}
            setSelectedFileIds={setSelectedFileIds}
            onDeleteSelectedFiles={openMultiDeleteModalForSelectedFiles}
            downloadProject={downloadProject}
            handleFramePrint={handleFramePrint}
            handlePrint={handlePrint}
            handleBatchPrint={handleBatchPrint}
            copyProject={copyProject}
            isPrinting={isPrinting}
          />

          <RightPanel
            user={user}
            selectedProjectName={selectedProjectName}
            checkedCount={checkedCount}
            sfile={sfile}
            selectedFile={selectedFile}
            isCreatingNew={isCreatingNew}
            t={t}
            dispatch={dispatch}
            parseAppType={parseAppType}
            setShowSameNameWarning={setShowSameNameWarning}
            showSameNameWarning={showSameNameWarning}
            keywordPillsRef={keywordPillsRef}
            setKeywordInputDirty={setKeywordInputDirty}
            configs_ui={configs_ui}
            setPendingMultiDelete={setPendingMultiDelete}
            setShowMultiDeleteModal={setShowMultiDeleteModal}
            showMultiDeleteModal={showMultiDeleteModal}
            pendingMultiDelete={pendingMultiDelete}
            deleteMultipleProjects={deleteMultipleProjects}
            setSelectedFileIds={setSelectedFileIds}
            getUser={getUser}
            selectedFileAppType={selectedFileAppType}
            copyOFCurrentSelectedFile={copyOFCurrentSelectedFile}
            access={access}
            config={config}
            showSaveButton={showSaveButton}
            updateFileMeta={updateFileMeta}
            refreshSelectedProject={refreshSelectedProject}
            setCopyOfCurrentSelectedFile={setCopyOfCurrentSelectedFile}
            setProjectName={setProjectName}
            setPendingDelete={setPendingDelete}
            setShowDeleteModal={setShowDeleteModal}
            showDeleteModal={showDeleteModal}
            pendingDelete={pendingDelete}
            nextFileListName={nextFileListName}
            deleteProject={deleteProject}
            fileTypeRef={fileTypeRef}
            mtrlTemplate={mtrlTemplate}
            setMtrlTemplate={setMtrlTemplate}
            projectName={projectName}
            createProject={createProject}
            parseUTC={parseUTC}
            setSelectedFile={setSelectedFile}
          />

          <ShareModal isShareModalOpen={isShareModalOpen} user={user} sfile={sfile} />
        </>
      )}
      {!user && (
        <Col>
          <Card className="glass-pane">
            <Card.Body>
              <h2>{t("welcome_to_the_project_explorer")}</h2>
              <p>{t("please_log_in_to_view_and_manage_your_projects")}</p>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
}
